import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ApiService from "../../Service/ApiService";
import DatePicker from "react-datepicker";
import { AiOutlineArrowLeft } from "react-icons/ai";
import formatCurrency from "../common/CurrencyFormatter";
import { notify } from "../common/Toast";
import { Modal } from "antd";

import "./index.css";
import calculateBookingCost from "../common/BookingCostCalculator";

const RoomDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roomId } = useParams();
  const [roomDetails, setRoomDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [checkInDate, setCheckInDate] = useState(
    location.state?.checkInDate ? new Date(location.state.checkInDate) : null
  );
  const [checkOutDate, setCheckOutDate] = useState(
    location.state?.checkOutDate ? new Date(location.state.checkOutDate) : null
  );
  const [numAdults, setNumAdults] = useState(1);
  const [numChildren, setNumChildren] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalGuests, setTotalGuests] = useState(0);
  const [availabilityStatus, setAvailabilityStatus] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(
    !!(location.state?.checkInDate && location.state?.checkOutDate)
  );
  const [userId, setUserId] = useState("");
  const [confirmClicked, setConfirmCLicked] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true); // set laoding state to true
      const response = await ApiService.getRoomById(roomId);
      setRoomDetails(response);
      const userProfile = await ApiService.getUserProfile();
      setUserId(userProfile.id);
      setIsLoading(false); // set loading state to false after fetching or error
    };
    fetchData();
  }, [roomId]); // Re-run effect when roomId changes

  useEffect(() => {
    const totalGuestsCount = numAdults + numChildren;
    setTotalGuests(totalGuestsCount);

    if (checkInDate && checkOutDate && roomDetails) {
      setTotalPrice(
        calculateBookingCost(roomDetails.roomPrice, checkInDate, checkOutDate),
      );

      const startDate = new Date(checkInDate);
      const endDate = new Date(checkOutDate);
      const formattedCheckInDate = new Date(
        startDate.getTime() - startDate.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .split("T")[0];
      const formattedCheckOutDate = new Date(
        endDate.getTime() - endDate.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .split("T")[0];

      if (roomDetails && !isRoomAvailableForRange(roomDetails, formattedCheckInDate, formattedCheckOutDate)) {
        setAvailabilityStatus("Selected date range overlaps an existing booking.");
      } else {
        setAvailabilityStatus("Selected date range is available for this room.");
      }
    } else {
      setTotalPrice(0);
      setAvailabilityStatus("");
    }
  }, [checkInDate, checkOutDate, numAdults, numChildren, roomDetails]);

  const isRoomAvailableForRange = (room, formattedCheckInDate, formattedCheckOutDate) => {
    if (!room?.bookings?.length) return true;

    const requestedStart = new Date(formattedCheckInDate);
    const requestedEnd = new Date(formattedCheckOutDate);

    return !room.bookings.some((booking) => {
      const existingStart = new Date(booking.checkInDate);
      const existingEnd = new Date(booking.checkOutDate);
      return (
        requestedStart < existingEnd && existingStart < requestedEnd
      );
    });
  };

  const handleConfirmBooking = () => {
    if (!checkInDate || !checkOutDate) {
      notify("Please select check-in and check-out dates.", "error");
      return;
    }
    if (isNaN(numAdults) || numAdults < 1 || isNaN(numChildren) || numChildren < 0) {
      notify("Please enter valid numbers for adults and children.", "error");
      return;
    }
    const totalGuests = numAdults + numChildren;
    if (roomDetails.maxPersons && totalGuests > roomDetails.maxPersons) {
      notify(
        `This room can accommodate up to ${roomDetails.maxPersons} guests only.`,
        "error",
      );
      return;
    }

    Modal.confirm({
      title: "Confirm Booking",
      content: `Book this room from ${checkInDate.toLocaleDateString()} to ${checkOutDate.toLocaleDateString()} for ${totalGuests} guest(s)?`,
      okText: "Book Now",
      cancelText: "Cancel",
      onOk: () => submitBooking(totalGuests),
    });
  };

  const submitBooking = async (totalGuests) => {
    setConfirmCLicked(true);

    const startDate = new Date(checkInDate);
    const endDate = new Date(checkOutDate);
    const formattedCheckInDate = new Date(
      startDate.getTime() - startDate.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];
    const formattedCheckOutDate = new Date(
      endDate.getTime() - endDate.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];

    if (!isRoomAvailableForRange(roomDetails, formattedCheckInDate, formattedCheckOutDate)) {
      notify("This room is not available for the selected dates.", "error");
      setConfirmCLicked(false);
      return;
    }

    const price = calculateBookingCost(
      roomDetails.roomPrice,
      checkInDate,
      checkOutDate,
    );

    const booking = {
      checkInDate: formattedCheckInDate,
      checkOutDate: formattedCheckOutDate,
      numOfAdults: numAdults,
      numOfChildren: numChildren,
      userId: userId,
      roomIds: [parseInt(roomId)],
      totalNumberOfGuests: totalGuests,
      totalPrice: price,
      totalCost: price,
    };

    const response = await ApiService.bookRoom(booking);
    setConfirmCLicked(false);
    if (response) {
      Modal.success({
        title: "Booking Confirmed!",
        content: `Your confirmation code is: ${response}`,
        okText: "View My Bookings",
        onOk: () => navigate("/find-booking"),
      });
    }
  };

  const cancelBooking = () => {
    setShowDatePicker(false);
    setCheckInDate(null);
    setCheckOutDate(null);
    setNumAdults(1);
    setNumChildren(0);
    setTotalPrice(0);
    setTotalGuests(0);
    setAvailabilityStatus("");
    setConfirmCLicked(false);
  };

  if (isLoading) {
    return <p className="room-detail-loading]">loading room details...</p>;
  }
  if (!roomDetails) {
    return <p className="room-detail-loading">Room not found</p>;
  }
  const { roomType, roomPrice, roomPhotoUrl, description, bookings } =
    roomDetails;
  
  return (
    <div className="room-details-booking">
      <div className="back-button">
        <AiOutlineArrowLeft
          onClick={() => navigate(-1)}
          size={25}
          style={{ cursor: "pointer" }}
        />
      </div>
      <div className="room-details-sections">
        <div className="room-details-card">
          <h2>Room Details </h2>
          <br />
          <img
            src={roomPhotoUrl}
            alt={roomType}
            className="room-details-image"
          />
          <div className="room-details-info">
            <div className="room-details-info-row">
              <h3>{roomType}</h3>
              <p>
                {formatCurrency(roomPrice, "en-US", roomDetails.currency)} <span>per night</span>
              </p>
            </div>
            <p>{description}</p>
          </div>
          
          <div className="booking-info">
            <button
              className="book-now-button"
              onClick={() => setShowDatePicker(true)}
            >
              {" "}
              Book Now
            </button>
          </div>
        </div>
        {showDatePicker && (
          <div className="room-details-card">
            <h2>Book This Room</h2>
            {showDatePicker && (
              <div className="date-picker-container">
                <DatePicker
                  className="detail-search-field"
                  selected={checkInDate}
                  onChange={(date) => setCheckInDate(date)}
                  selectsStart
                  startDate={checkInDate}
                  endDate={checkOutDate}
                  minDate={new Date()}
                  placeholderText="Check-in Date"
                  dateFormat="dd/MM/yyyy"
                />
                <DatePicker
                  className="detail-search-field"
                  selected={checkOutDate}
                  onChange={(date) => setCheckOutDate(date)}
                  selectsEnd
                  startDate={checkInDate}
                  endDate={checkOutDate}
                  minDate={checkInDate}
                  placeholderText="Check-out Date"
                  dateFormat="dd/MM/yyyy"
                />

                <div className="guest-container">
                  <div className="guest-div">
                    <label>Adults: </label>
                    <input
                      type="number"
                      min="0"
                      value={numAdults}
                      onChange={(e) => setNumAdults(parseInt(e.target.value))}
                    />
                  </div>

                  <div className="guest-div">
                    <label>Children: </label>
                    <input
                      type="number"
                      min="0"
                      value={numChildren}
                      onChange={(e) => setNumChildren(parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            )}
            {showDatePicker && availabilityStatus && (
              <div className="room-details-status">
                <p>
                  <strong>Availability Status:</strong> {availabilityStatus}
                </p>
                <p>
                  <strong>Room Capacity:</strong> {roomDetails.maxPersons} guests
                </p>
              </div>
            )}
            {totalPrice > 0 && showDatePicker && (
              <div className="room-details-card booking-details-confirm-container">
                <h2>Booking Details</h2>
                <p>
                  Check In <span>{checkInDate?.toLocaleDateString()}</span>
                </p>
                <p>
                  Check out <span>{checkOutDate?.toLocaleDateString()}</span>
                </p>
                <p>
                  Total Guests <span>{totalGuests}</span>
                </p>
                <p>
                  Total Price <span>{formatCurrency(totalPrice, "en-US", roomDetails.currency)}*</span>
                </p>
                <p className="estimate-message">
                  The cost shown is an estimate of what is the expected total
                  cost
                </p>
                <div className="booking-summary-actions">
                  <button
                    type="button"
                    onClick={handleConfirmBooking}
                    className="accept-booking"
                    disabled={confirmClicked}
                  >
                    {confirmClicked ? "Processing..." : "Confirm Booking"}
                  </button>
                  <button
                    type="button"
                    onClick={cancelBooking}
                    className="delete-booking"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomDetailsPage;
